import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { lastFirst } from "@/lib/names";
import { ArrowLeft, EnvelopeSimple, Phone, MapPin, ClockCounterClockwise, FilePdf } from "@phosphor-icons/react";
import { money } from "@/lib/money";

const fmt$ = money;
const KIND_LABELS = {
  timesheet: "Weekly pay", weekly_flat: "Flat weekly", salary: "Salary",
  per_billed_hour: "Commission", distribution: "Distribution", distribution_extra: "Extra distribution",
};

const Stat = ({ label, value, testid }) => (
  <div className="bg-white border border-slate-200 rounded-md px-4 py-3" data-testid={testid}>
    <div className="tinylabel">{label}</div>
    <div className="text-xl font-black mt-0.5">{value}</div>
  </div>
);

export default function WorkerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/workers/${id}/timeline`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [id]);

  const weeks = useMemo(() => {
    if (!data) return [];
    const map = {};
    const wk = (k) => (map[k] = map[k] || { week_start: k, timesheets: [], pay: [] });
    data.timesheets.forEach((t) => wk(t.week_start).timesheets.push(t));
    data.pay.forEach((p) => wk(p.week_start || "—").pay.push(p));
    return Object.values(map).sort((a, b) => (b.week_start || "").localeCompare(a.week_start || ""));
  }, [data]);

  if (err) return <div className="p-8 text-red-600" data-testid="worker-detail-error">{err}</div>;
  if (!data) return <div className="p-8 text-slate-400">Loading…</div>;
  const { worker: w, assignments, totals } = data;

  return (
    <div className="p-4 md:p-8" data-testid="worker-detail-page">
      <div className="flex items-center justify-between mb-4">
        <Link to="/workers" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 font-semibold">
          <ArrowLeft size={14} /> Workers
        </Link>
        <a data-testid="export-timeline-pdf" href={`${API_BASE}/workers/${id}/timeline.pdf`}
          target="_blank" rel="noreferrer"
          className="h-10 px-4 border border-slate-300 rounded-md text-sm font-semibold inline-flex items-center gap-2 hover:bg-slate-50">
          <FilePdf size={16} /> Export PDF
        </a>
      </div>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="tinylabel">Worker history</div>
          <h1 className="text-3xl font-black flex items-center gap-3" data-testid="worker-detail-name">
            {lastFirst(w.name)} <StatusPill status={w.status} />
          </h1>
          {w.dba && <div className="text-sm text-slate-500 mt-1">dba {w.dba}</div>}
          <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-600">
            {w.email && <span className="inline-flex items-center gap-1"><EnvelopeSimple size={15} /> {w.email}</span>}
            {w.phone && <span className="inline-flex items-center gap-1"><Phone size={15} /> {w.phone}</span>}
            {w.address && <span className="inline-flex items-center gap-1"><MapPin size={15} /> {w.address}</span>}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Assignments" value={totals.assignments} testid="stat-assignments" />
          <Stat label="Weeks worked" value={totals.weeks_worked} testid="stat-weeks" />
          <Stat label="Lifetime hours" value={totals.hours.toLocaleString()} testid="stat-hours" />
          <Stat label="Gross paid" value={fmt$(totals.gross_paid)} testid="stat-gross-paid" />
        </div>
      </div>
      {totals.gross_pending > 0 && (
        <div className="mb-6 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 inline-block" data-testid="pending-pay-note">
          {fmt$(totals.gross_pending)} more sits on draft/approved (not yet paid) pay runs.
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md mb-8 overflow-x-auto" data-testid="worker-assignments-card">
        <div className="px-4 pt-4 tinylabel">Assignments</div>
        <table className="w-full text-sm mt-2">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-2">Project</th><th className="text-left px-4 py-2">Client</th>
              <th className="text-right px-4 py-2">Pay</th><th className="text-right px-4 py-2">Bill</th>
              <th className="text-right px-4 py-2">Per diem</th>
              <th className="text-left px-4 py-2">Start</th><th className="text-left px-4 py-2">End</th>
              <th className="text-left px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {assignments.length === 0 && <tr><td colSpan={8} className="px-4 py-4 text-slate-400">No assignments yet.</td></tr>}
            {assignments.map((a) => (
              <tr key={a.id} className="border-t border-slate-100" data-testid={`timeline-assignment-${a.id}`}>
                <td className="px-4 py-2 font-medium">{a._project_name || "—"}</td>
                <td className="px-4 py-2 text-slate-600">{a._client_name || "—"}</td>
                <td className="px-4 py-2 text-right">{fmt$(a.pay_rate)}/hr</td>
                <td className="px-4 py-2 text-right">{fmt$(a.bill_rate)}/hr</td>
                <td className="px-4 py-2 text-right">{a.per_diem_rate ? fmt$(a.per_diem_rate) : "—"}</td>
                <td className="px-4 py-2 text-slate-600">{a.start_date || "—"}</td>
                <td className="px-4 py-2 text-slate-600">{a.end_date || "—"}</td>
                <td className="px-4 py-2"><StatusPill kind="assignment" status={a.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="tinylabel mb-3 flex items-center gap-2">
        <ClockCounterClockwise size={16} /> Weekly timeline
      </div>
      <div className="space-y-4" data-testid="worker-timeline">
        {weeks.length === 0 && <div className="text-slate-400 text-sm">No timesheets or pay history yet.</div>}
        {weeks.map((wk) => (
          <div key={wk.week_start} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`timeline-week-${wk.week_start}`}>
            <div className="font-bold text-slate-800 mb-2">Week of {wk.week_start}</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Timesheets</div>
                {wk.timesheets.length === 0 && <div className="text-xs text-slate-400">None</div>}
                {wk.timesheets.map((t) => (
                  <div key={t.id} className="flex flex-wrap items-center gap-2 py-1 text-sm border-b border-slate-50 last:border-0">
                    <span className="font-medium">{t._project_name || "—"}</span>
                    <span className="text-slate-500">{(t.total_hours || 0).toLocaleString()} hrs</span>
                    <StatusPill status={t.status} />
                    {t.billing_only && <span className="pill pill-billing-only">BILLING ONLY</span>}
                    {t.billing_excluded && <span className="pill pill-excluded">NOT BILLED</span>}
                    {t._invoice_number && (
                      <Link to={`/invoices/${t.invoice_id}`} className="text-xs underline text-slate-500 hover:text-green-700">
                        {t._invoice_number}
                      </Link>
                    )}
                    {t.covered_by_invoice_number && (
                      <span className="text-xs text-slate-400" title={t.variance_note || ""}>
                        ↳ covered by <Link to={`/invoices/${t.covered_by_invoice_id}`} className="underline">{t.covered_by_invoice_number}</Link>
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Pay</div>
                {wk.pay.length === 0 && <div className="text-xs text-slate-400">Not in a pay run</div>}
                {wk.pay.map((p, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 py-1 text-sm border-b border-slate-50 last:border-0">
                    <span className="font-medium">{KIND_LABELS[p.kind] || p.kind}</span>
                    {p.total_hours > 0 && <span className="text-slate-500">{p.total_hours.toLocaleString()} hrs</span>}
                    <span className="font-bold">{fmt$(p.gross_pay)}</span>
                    <StatusPill status={p.payrun_status} />
                    {p.note && <span className="text-xs text-slate-400" title={p.note}>({p.note})</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
